import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaClipboardList, FaTasks, FaChevronDown, FaChevronRight, FaCalendarAlt, FaExclamationTriangle, FaCheck } from 'react-icons/fa';
import TaskDetailsModal from './components/TaskDetailsModal';
import CreateTaskModal from './components/CreateTaskModal';
import KanbanBoard from './components/KanbanBoard';
import { clampTaskDateUpdates } from '../../utils/fsClamp';
import { confirmProjectWindow } from '../../utils/projectWindow';
import { confirmClampAdjustments, alertImpossibleRange, alertEndBeforeStart } from '../../utils/clampFeedback';
import { useProjectData } from './context/ProjectDataContext';
import useTaskOperations from './hooks/useTaskOperations';
import useComments from './hooks/useComments';
import { apiFetch } from '../../utils/apiClient';
import Tooltip from '../../components/Tooltip';

// Get gradient for progress bar (matches Gantt style)
const getProgressGradient = (baseColour) => {
    const lighterVersions = {
        '#64748b': '#94a3b8', // closed - slate
        '#5b9bd5': '#8bbef0', // in progress - blue
        '#9775c4': '#b99de0', // review - purple
        '#5eada4': '#7bc4bc', // ready - muted teal
        '#a1a1aa': '#b0b0b8', // new - grey
        '#dc7c7c': '#e8a0a0', // needs info - red
        '#7c6b9e': '#a090c0', // status row - purple
        '#10b981': '#22c58a', // main task - green
    };
    const lighter = lighterVersions[baseColour] || baseColour;
    return `linear-gradient(to right, ${lighter} 0%, ${baseColour} 100%)`;
};

// Get subtask progress percentage and colour based on sprint workflow position
const getSubtaskProgress = (statusName, isClosed) => {
    if (isClosed) return { percent: 100, colour: '#64748b' }; // Closed = 100%
    const statusLower = (statusName || '').toLowerCase();
    if (statusLower.includes('ready') || statusLower.includes('done')) return { percent: 80, colour: '#5eada4' };
    if (statusLower.includes('review') || statusLower.includes('test')) return { percent: 60, colour: '#9775c4' };
    if (statusLower.includes('progress') || statusLower.includes('doing')) return { percent: 40, colour: '#5b9bd5' };
    if (statusLower.includes('needs info')) return { percent: 20, colour: '#dc7c7c', needsInfo: true };
    return { percent: 0, colour: '#a1a1aa' }; // New = 0% (not started)
};

// Helper to format date as UK format (DD/MM/YYYY)
const formatDateUK = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

// Helper to check if date is overdue
const isOverdue = (dateString) => {
    if (!dateString) return false;
    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
};

// Helper to check if date is within 2 days
const isWithin2Days = (dateString) => {
    if (!dateString) return false;
    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 2;
};

// Helper to get due date colour class
const getDueDateColour = (dateString) => {
    if (!dateString) return '';
    if (isOverdue(dateString)) return 'text-red-600';
    if (isWithin2Days(dateString)) return 'text-orange-500';
    return 'text-green-600';
};

// Helper to get due date background colour class
// Red: overdue, Orange: within 2 days, Grey: more than 2 days away
const getDueDateBgColour = (dateString) => {
    if (!dateString) return '';
    if (isOverdue(dateString)) return 'bg-red-100 text-red-700';
    if (isWithin2Days(dateString)) return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-700'; // Far in future - not urgent
};

// Helper to get start date background colour class
// Grey: more than 3 days away, Orange: within 3 days, Green: started (today or past)
const getStartDateBgColour = (dateString) => {
    if (!dateString) return 'bg-gray-100 text-gray-500';
    const startDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    const diffDays = (startDate - today) / (1000 * 60 * 60 * 24);

    if (diffDays <= 0) return 'bg-green-100 text-green-700'; // Started (today or past)
    if (diffDays <= 3) return 'bg-orange-100 text-orange-700'; // Within 3 days
    return 'bg-gray-100 text-gray-500'; // More than 3 days away
};

// Calculate effective date range from subtasks
const getEffectiveDateRange = (task, allTasks) => {
    const subtasks = allTasks.filter(t => t.mainTaskId === task.taskId);
    if (subtasks.length === 0) {
        // No subtasks - use task's own dates
        return { start: task.startDate, end: task.dueDate };
    }

    // Find earliest start date from subtasks
    const startDates = subtasks
        .map(st => st.startDate)
        .filter(Boolean)
        .map(d => new Date(d));
    const start = startDates.length > 0
        ? new Date(Math.min(...startDates)).toISOString().split('T')[0]
        : null;

    // Find latest due date from subtasks
    const endDates = subtasks
        .map(st => st.dueDate)
        .filter(Boolean)
        .map(d => new Date(d));
    const end = endDates.length > 0
        ? new Date(Math.max(...endDates)).toISOString().split('T')[0]
        : null;

    return { start, end };
};

const ClientDashboard = () => {
    const token = localStorage.getItem('clientToken');
    const leadId = localStorage.getItem('clientLeadId');
    const timestamp = localStorage.getItem('clientTimestamp');

    // Project data from shared context
    const {
        loading,
        project,
        tasks,
        setTasks,
        links,
        columns,
        taskStatuses,
        epics,
        members,
        lead,
        teamSwimlaneId,
        error,
        setError,
        fetchProjectData,
        updateInProgressRef,
        selectedTaskRef,
        lastUpdate,
        preventRefreshRef,
        blockedTasksMap
    } = useProjectData();

    // Task operations hook
    const {
        selectedTaskDetail,
        setSelectedTaskDetail,
        loadingTaskDetail,
        savingChanges,
        savingField,
        creatingTask,
        creatingSubtask,
        fetchTaskDetail,
        handleSaveChanges,
        handleDeleteTask,
        handleCreateTask,
        handleCreateSubtask,
        handleDrop
    } = useTaskOperations(
        token,
        leadId,
        timestamp,
        project,
        teamSwimlaneId,
        setTasks,
        fetchProjectData
    );

    // Comments hook
    const {
        addingComment,
        editingComment,
        deletingComment,
        handleAddComment,
        handleEditComment,
        handleDeleteComment
    } = useComments(token, leadId, timestamp, fetchTaskDetail);

    // Check if user is admin
    const isAdmin = lead?.clientRole === 'admin';

    // Remaining Dashboard state
    const navigate = useNavigate();
    const [draggedTask, setDraggedTask] = useState(null);
    // ⛔ THE REF, NOT THE STATE, FEEDS THE DROP. The touch path calls
    // onDragStart and onDrop in the SAME tick (KanbanBoard's long-press lift),
    // so the onDrop closure below still holds the PREVIOUS render's
    // draggedTask - null on a first drag (a silent no-op) or the last-dragged
    // task (moves the WRONG card). The mouse path only ever worked because
    // dragstart and drop are separate event loops with renders between; the ref
    // removes that timing dependence for both paths.
    const draggedTaskRef = useRef(null);
    const [collapsedColumns, setCollapsedColumns] = useState(() => {
        const saved = localStorage.getItem('collapsedEpics');
        return saved ? new Set(JSON.parse(saved)) : new Set(['Archived']);
    });
    const edgeColumnsInitialisedRef = useRef(localStorage.getItem('collapsedEpics') !== null);
    const [collapsedSwimlanes, setCollapsedSwimlanes] = useState(() => {
        const saved = localStorage.getItem('collapsedSwimlanes');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });
    const [epicsCollapsed, setEpicsCollapsed] = useState(() => {
        const saved = localStorage.getItem('epicsCollapsed');
        return saved !== null ? JSON.parse(saved) : true;
    });
    // Hide completed main tasks in the Epics section (persisted, defaults to showing them)
    const [hideCompletedEpics, setHideCompletedEpics] = useState(() => {
        return localStorage.getItem('epicsHideCompleted') === 'true';
    });
    useEffect(() => {
        localStorage.setItem('epicsHideCompleted', hideCompletedEpics.toString());
    }, [hideCompletedEpics]);

    const [hoveredMainTaskId, setHoveredMainTaskId] = useState(null);
    const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
    const [projectMembers, setProjectMembers] = useState([]);
    const [blockedTooltip, setBlockedTooltip] = useState(null);
    const blockedTooltipTimeoutRef = useRef(null);

    // Persist collapsed epics to localStorage
    useEffect(() => {
        localStorage.setItem('collapsedEpics', JSON.stringify([...collapsedColumns]));
    }, [collapsedColumns]);

    // Collapse Edge columns by default on first load (when no saved state exists)
    useEffect(() => {
        if (!edgeColumnsInitialisedRef.current && columns.length > 0) {
            const edgeColumns = columns.filter(c => c.name.startsWith('Edge')).map(c => c.name);
            if (edgeColumns.length > 0) {
                setCollapsedColumns(prev => {
                    const newSet = new Set(prev);
                    edgeColumns.forEach(name => newSet.add(name));
                    return newSet;
                });
            }
            edgeColumnsInitialisedRef.current = true;
        }
    }, [columns]);

    // Persist collapsed swimlanes to localStorage
    useEffect(() => {
        localStorage.setItem('collapsedSwimlanes', JSON.stringify([...collapsedSwimlanes]));
    }, [collapsedSwimlanes]);

    // Persist epics section collapsed state
    useEffect(() => {
        localStorage.setItem('epicsCollapsed', JSON.stringify(epicsCollapsed));
    }, [epicsCollapsed]);

    // Bulk expand/collapse for the Epics section, mirroring the Gantt toolbar.
    // NOTE the inverted convention here: a key PRESENT in collapsedColumns means
    // that row is EXPANDED (the Gantt's expandedItems defaults the other way).
    // Rows appear under both groupings, so both key shapes are written:
    // status-/maintask- for the Status groups, epic-/epic-maintask- for the
    // Epic groups. Toggle rule matches the Gantt: anything expanded collapses
    // everything, otherwise expand everything.
    const toggleAllEpicRows = (type, swimlaneFilter = null) => {
        setCollapsedColumns(prev => {
            const keys = [];
            // Groups holding the matched tasks. A main-task row only renders
            // inside an expanded group, so expanding tasks while their group is
            // shut would change nothing on screen - open the ancestors too.
            const ancestorKeys = [];

            if (type === 'group') {
                columns.filter(status => !status.is_archived).forEach(status => keys.push(`status-${status.epicId}`));
                epics.forEach(epic => keys.push(`epic-${epic.epicId}`));
            } else {
                (tasks || []).filter(t => !t.mainTaskId).forEach(mainTask => {
                    const isTeamTask = mainTask.swimlaneId === teamSwimlaneId ||
                        (mainTask.swimlaneId === null && teamSwimlaneId === project?.default_swimlane);
                    if (swimlaneFilter === 'team' && !isTeamTask) return;
                    if (swimlaneFilter === 'tme' && isTeamTask) return;
                    keys.push(`maintask-${mainTask.taskId}`);
                    keys.push(`epic-maintask-${mainTask.taskId}`);
                    if (mainTask.epicId) ancestorKeys.push(`status-${mainTask.epicId}`);
                    (mainTask.epics || []).forEach(e => ancestorKeys.push(`epic-${e.id}`));
                });
            }

            const anyExpanded = keys.some(key => prev.has(key));
            const next = new Set(prev);
            keys.forEach(key => anyExpanded ? next.delete(key) : next.add(key));
            // Collapsing leaves the groups as the user had them - only the
            // expand direction needs the ancestors opened.
            if (!anyExpanded) ancestorKeys.forEach(key => next.add(key));
            return next;
        });
    };


    // Auto-refresh task detail when WebSocket triggers update
    const lastUpdateRef = useRef(lastUpdate);
    useEffect(() => {
        // Only refresh if lastUpdate actually changed (WebSocket fired)
        // Skip if preventRefreshRef is set (modal was just closed)
        if (selectedTaskDetail && lastUpdate && lastUpdate !== lastUpdateRef.current && !preventRefreshRef.current) {
            lastUpdateRef.current = lastUpdate;
            fetchTaskDetail(selectedTaskDetail.taskId, false);
        }
    }, [lastUpdate, selectedTaskDetail, fetchTaskDetail]);

    // Fetch project members when task detail modal opens
    useEffect(() => { if (selectedTaskDetail) handleOpenAssignmentEditor(); }, [selectedTaskDetail]);

    // Listen for task click events from Project Links modal
    useEffect(() => {
        const handleOpenTaskFromLinks = (event) => {
            const { taskId } = event.detail;
            if (taskId) {
                fetchTaskDetail(taskId);
            }
        };
        window.addEventListener('openTaskFromLinks', handleOpenTaskFromLinks);
        return () => window.removeEventListener('openTaskFromLinks', handleOpenTaskFromLinks);
    }, [fetchTaskDetail]);

    const canUserDelete = (item) => {
        if (!lead || !lead.email) return false;

        if (item.assignedTo && String(item.assignedTo) === String(lead.email)) {
            return true;
        }

        if (item.assignedUsers && item.assignedUsers.some(u => String(u) === String(lead.email))) {
            return true;
        }

        return false;
    };

    const handleDragStart = (task) => {
        // Only allow dragging if task is in Team swimlane
        if (task.swimlaneId === teamSwimlaneId ||
            (task.swimlaneId === null && teamSwimlaneId === project?.default_swimlane)) {
            draggedTaskRef.current = task;
            setDraggedTask(task);
            }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleOpenCreateModal = async () => {
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/project-members?leadId=${leadId}&timestamp=${timestamp}`
            );
            const data = await response.json();
            if (data.members) {
                setProjectMembers(data.members);
            }
        } catch (err) {
            console.error('Failed to fetch project members:', err);
        }
        setShowCreateTaskModal(true);
    };

    // Fetch members when opening assignment editor
    const handleOpenAssignmentEditor = async () => {
        console.log('Opening assignment editor, fetching members...');
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/project-members?leadId=${leadId}&timestamp=${timestamp}`
            );
            const data = await response.json();
            console.log('Project members fetched:', data);
            if (data.members) {
                setProjectMembers(data.members);
            }
        } catch (err) {
            console.error('Failed to fetch project members:', err);
        }
    };

    // Map project members to members for assignment dropdown
    const clientUsers = projectMembers
    .map(pm => {
        // Find matching member by leadId
        return members.find(m => m.leadId === pm.leadId);
    })
    .filter(m => m); // Remove any that weren't found

    // Blocked tooltip handlers
    const handleBlockedMouseEnter = (e, blockers) => {
        if (blockedTooltipTimeoutRef.current) clearTimeout(blockedTooltipTimeoutRef.current);
        const rect = e.currentTarget.getBoundingClientRect();
        blockedTooltipTimeoutRef.current = setTimeout(() => {
            setBlockedTooltip({
                content: blockers,
                x: rect.left + rect.width / 2,
                y: rect.top - 8
            });
        }, 200);
    };

    const handleBlockedMouseLeave = () => {
        if (blockedTooltipTimeoutRef.current) clearTimeout(blockedTooltipTimeoutRef.current);
        blockedTooltipTimeoutRef.current = setTimeout(() => {
            setBlockedTooltip(null);
        }, 100);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading Kanban...</p>
            </div>
            </div>
        );
    }
    if (error && !project) {
        return (
            <div className="h-full flex items-center justify-center bg-gray-50">
            <div className="text-center">
            <div className="text-xl text-red-600 mb-4">{error}</div>
            <button
            onClick={() => window.location.reload()}
            className="btn-primary"
            >
            Retry
            </button>
            </div>
            </div>
        );
    }

    // Calculate project progress from subtasks
    const subtasks = tasks.filter(t => t.mainTaskId); // Only subtasks have mainTaskId set
    const totalSubtasks = subtasks.length;
    const completedSubtasks = subtasks.filter(t => t.isClosed).length;
    const progressPercent = totalSubtasks > 0 ? Math.round((completedSubtasks / totalSubtasks) * 100) : 0;

    // Calculate heatmap color (red → yellow → green)
    const getProgressColor = (percent) => {
        if (percent <= 50) {
            // Amber to Yellow (0-50%)
            const ratio = percent / 50;
            const r = Math.round(251 - (251 - 250) * ratio); // 251 to 250
            const g = Math.round(146 + (204 - 146) * ratio); // 146 to 204
            const b = Math.round(60 - (60 - 21) * ratio); // 60 to 21
            return `rgb(${r}, ${g}, ${b})`;
        } else {
            // Yellow to Green (50-100%)
            const ratio = (percent - 50) / 50;
            const r = Math.round(250 - (250 - 34) * ratio); // 250 to 34
            const g = Math.round(204 - (204 - 197) * ratio); // 204 to 197
            const b = Math.round(21 + (94 - 21) * ratio); // 21 to 94
            return `rgb(${r}, ${g}, ${b})`;
        }
    };

    return (
        <div className="h-full flex flex-col overflow-hidden">
        {/* Project Progress Bar */}
        {totalSubtasks > 0 && (
            <div className="w-full bg-gradient-to-b from-gray-50 to-gray-200 border-t border-b border-t-gray-100 border-b-gray-400 px-4 py-2 shadow-[0_3px_5px_rgba(0,0,0,0.15)]">
            <div className="flex items-center gap-3">
            <span className="text-[13px] text-gray-500 whitespace-nowrap">Overall Project Progress ({totalSubtasks - completedSubtasks} Outstanding, {completedSubtasks} Closed Tasks)</span>
            <div className="flex-1 h-2 bg-gray-300 rounded-full overflow-hidden">
            <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                    width: `${progressPercent}%`,
                    backgroundColor: getProgressColor(progressPercent),
                    backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.35), rgba(0,0,0,0.12))'
                }}
            />
            </div>
            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{progressPercent}%</span>
            </div>
            </div>
        )}

        {/* Error Banner */}
        {error && (
            <div className="bg-red-50 border-b border-red-200">
            <div className="max-w-7xl mx-auto px-4 py-2">
            <p className="text-sm text-red-700">{error}</p>
            </div>
            </div>
        )}

        {/* Epics Section - Groups tasks by Status (Kanban column) */}
        {columns.length > 0 && (() => {
            // Calculate visible status count (statuses with tasks that have subtasks)
            const visibleStatusCount = columns
                .filter(status => !status.is_archived)
                .filter(status => {
                    const statusTasks = (tasks?.filter(t => !t.mainTaskId && t.epicId === status.epicId) || [])
                        .filter(mainTask => tasks.some(t => t.mainTaskId === mainTask.taskId));
                    return statusTasks.length > 0;
                }).length;

            return (
            <div className="w-full mx-auto p-2">
            <div className="bg-white rounded-lg shadow">
            {/* Epics Header */}
            <div className="px-4 py-2 border-b border-gray-200 flex justify-between items-center">
            <div
            className="flex items-center gap-2 flex-1"
            >
            <span
            className="cursor-pointer p-1 hover:bg-gray-100 rounded"
            onClick={() => setEpicsCollapsed(!epicsCollapsed)}
            >
            {epicsCollapsed ? <FaChevronRight /> : <FaChevronDown />}
            </span>
            <h2 className="text-lg font-semibold text-gray-900">Epics</h2>
            <span className="text-sm text-gray-500">({visibleStatusCount})</span>
            </div>
            <div className="flex items-center gap-2">
            {/* Bulk expand/collapse - same set as the Gantt toolbar */}
            <span className="text-sm font-medium text-gray-700">Expand/Collapse:</span>
            <div className="flex gap-1">
                <Tooltip text="Expand/Collapse all Epics (Status groups)">
                    <button
                        onClick={() => toggleAllEpicRows('group')}
                        className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
                    >
                        Epics
                    </button>
                </Tooltip>
                <Tooltip text="Expand/Collapse all Main Tasks">
                    <button
                        onClick={() => toggleAllEpicRows('task')}
                        className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
                    >
                        Main Tasks
                    </button>
                </Tooltip>
                <Tooltip text="Expand/Collapse T.M.E. Tasks">
                    <button
                        onClick={() => toggleAllEpicRows('task', 'tme')}
                        className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
                    >
                        T.M.E. Tasks
                    </button>
                </Tooltip>
                <Tooltip text="Expand/Collapse Client Tasks">
                    <button
                        onClick={() => toggleAllEpicRows('task', 'team')}
                        className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
                    >
                        Client Tasks
                    </button>
                </Tooltip>
                <Tooltip text={epicsCollapsed ? 'Expand the Epics section' : 'Collapse the Epics section'}>
                    <button
                        onClick={() => setEpicsCollapsed(!epicsCollapsed)}
                        className="px-2 py-1 text-xs font-medium rounded bg-gray-300 text-gray-700 hover:bg-gray-400"
                    >
                        {epicsCollapsed ? 'Expand All' : 'Collapse All'}
                    </button>
                </Tooltip>
            </div>
            <button
                onClick={() => setHideCompletedEpics(!hideCompletedEpics)}
                className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                    hideCompletedEpics
                        ? 'border-primary-600 bg-primary-600 text-white hover:bg-primary-700'
                        : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-600'
                }`}
                title={hideCompletedEpics ? 'Show completed main tasks' : 'Hide completed main tasks'}
            >
                <FaCheck className="w-3 h-3" />
                <span>Hide completed</span>
            </button>
            </div>
            </div>

            {/* Epics Table */}
            {!epicsCollapsed && (
                <div>
                <table className="w-full table-fixed">
                <thead className="bg-gray-50 border-b">
                <tr>
                <th className="w-8 px-2 py-3"></th>
                <th className="w-[30%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="w-[12%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                <th className="w-[12%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                <th className="w-[12%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned</th>
                <th className="w-[12%] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sprint/Status</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {columns
                    .sort((a, b) => a.order - b.order)
                    .filter((status) => {
                        // Hide archived statuses and statuses with no tasks that have subtasks
                        if (status.is_archived) return false;
                        const statusTasks = (tasks?.filter(t => !t.mainTaskId && t.epicId === status.epicId) || [])
                            .filter(mainTask => tasks.some(t => t.mainTaskId === mainTask.taskId));
                        return statusTasks.length > 0;
                    })
                    .map((status) => {
                    // Unfiltered set - drives the completed-group marker, which must
                    // stay true whether or not the hide toggle is on
                    const allStatusTasks = (tasks?.filter(t => !t.mainTaskId && t.epicId === status.epicId) || [])
                        .filter(mainTask => tasks.some(t => t.mainTaskId === mainTask.taskId));
                    const groupComplete = allStatusTasks.length > 0 && allStatusTasks.every(t => t.isClosed === true);

                    // Visible set - rows, counts and progress all follow the toggle.
                    // NOTE: the .filter() above deliberately stays on the unfiltered
                    // list, so a fully hidden group still renders its header.
                    const statusTasks = hideCompletedEpics
                        ? allStatusTasks.filter(t => t.isClosed !== true)
                        : allStatusTasks;
                    const isExpanded = collapsedColumns.has(`status-${status.epicId}`);

                    // Calculate progress from all subtasks of tasks in this status
                    const allSubtasksForStatus = tasks?.filter(t =>
                        t.mainTaskId && statusTasks.some(st => st.taskId === t.mainTaskId)
                    ) || [];
                    const totalSubtasks = allSubtasksForStatus.length;
                    const closedSubtasks = allSubtasksForStatus.filter(t => t.isClosed).length;
                    const statusProgress = totalSubtasks > 0 ? Math.round((closedSubtasks / totalSubtasks) * 100) : 0;

                    return (
                        <React.Fragment key={status.epicId}>
                        {/* Status Row */}
                        <tr
                            className="hover:bg-purple-100 font-medium"
                            style={{
                                background: 'linear-gradient(to bottom, #faf7ff 0%, #f5f5f6 100%)',
                                boxShadow: 'inset 0 2px 0 0 #e9d5ff, inset 0 -1px 0 0 #e9d5ff'
                            }}
                        >
                        <td className="px-2 py-3">
                        <button
                        onClick={() => {
                            const newCollapsed = new Set(collapsedColumns);
                            if (isExpanded) {
                                newCollapsed.delete(`status-${status.epicId}`);
                            } else {
                                newCollapsed.add(`status-${status.epicId}`);
                            }
                            setCollapsedColumns(newCollapsed);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                        >
                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        </button>
                        </td>
                        <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                        {groupComplete && <FaCheck className="text-green-600 flex-shrink-0" size={12} />}
                        <span className={`font-medium ${groupComplete ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{status.name}</span>
                        <span className="text-xs text-gray-500">({statusTasks.length})</span>
                        </div>
                        </td>
                        <td className="px-3 py-3">
                        <span className="text-sm text-gray-400">-</span>
                        </td>
                        <td className="px-3 py-3">
                        <span className="text-sm text-gray-400">-</span>
                        </td>
                        <td className="px-3 py-3">
                        <span className="text-sm text-gray-400">-</span>
                        </td>
                        <td className="px-3 py-3">
                        <span className="text-sm text-gray-400">-</span>
                        </td>
                        <td className="px-3 py-3">
                        {groupComplete ? (
                        <span className="text-xs text-gray-500">
                        {closedSubtasks}/{totalSubtasks} Sub Tasks Completed
                        </span>
                        ) : (
                        <div className="flex items-center gap-3">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                        className="h-2 rounded-full transition-all"
                        style={{ width: `${statusProgress}%`, background: getProgressGradient('#7c6b9e') }}
                        />
                        </div>
                        <span className="text-xs text-gray-600 min-w-[3rem] text-right">
                        {statusProgress}%
                        </span>
                        </div>
                        )}
                        </td>
                        </tr>

                        {/* Expanded Tasks within this status - sorted to match Gantt order */}
                        {isExpanded && [...statusTasks].sort((a, b) => {
                            const swimlaneDiff = (a.swimlaneOrder || 0) - (b.swimlaneOrder || 0);
                            if (swimlaneDiff !== 0) return swimlaneDiff;
                            return (a.order || 0) - (b.order || 0);
                        }).map((mainTask) => {
                            // Get ALL subtasks for progress calculation
                            const allSubtasks = tasks.filter(t => t.mainTaskId === mainTask.taskId);
                            const isMainTaskExpanded = collapsedColumns.has(`maintask-${mainTask.taskId}`);
                            const isTeamTask = mainTask.swimlaneId === teamSwimlaneId || (mainTask.swimlaneId === null && teamSwimlaneId === project?.default_swimlane);

                            return (
                                <React.Fragment key={mainTask.taskId}>
                                {/* Main Task Row */}
                                <tr
                                    className={`hover:bg-gray-100 ${hoveredMainTaskId === mainTask.taskId ? 'bg-yellow-50 shadow-[inset_4px_0_0_0_#facc15]' : ''}`}
                                    style={hoveredMainTaskId !== mainTask.taskId ? {
                                        background: isTeamTask ? 'linear-gradient(to bottom, #eff6ff 0%, #f3f4f6 100%)' : 'linear-gradient(to bottom, #f3fef6 0%, #f5f5f6 100%)',
                                        boxShadow: isTeamTask
                                            ? 'inset 3px 0 0 0 #3b82f6, inset 0 2px 0 0 #93c5fd'
                                            : 'inset 0 2px 0 0 #86efac'
                                    } : {}}
                                >
                                <td className="px-2 py-2">
                                {allSubtasks.length > 0 && (
                                    <button
                                    onClick={() => {
                                        const newCollapsed = new Set(collapsedColumns);
                                        if (isMainTaskExpanded) {
                                            newCollapsed.delete(`maintask-${mainTask.taskId}`);
                                        } else {
                                            newCollapsed.add(`maintask-${mainTask.taskId}`);
                                        }
                                        setCollapsedColumns(newCollapsed);
                                    }}
                                    className="text-gray-600 hover:text-gray-900 ml-2"
                                    >
                                    {isMainTaskExpanded ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}
                                    </button>
                                )}
                                </td>
                                <td className="px-3 py-2">
                                <div className="flex items-center gap-2 pl-4">
                                {mainTask.isClosed && <FaCheck className="text-green-600 flex-shrink-0" size={11} />}
                                <span
                                className={`text-sm hover:text-blue-600 cursor-pointer ${mainTask.isClosed ? 'text-gray-400 line-through' : 'text-gray-700'}`}
                                onMouseEnter={() => setHoveredMainTaskId(mainTask.taskId)}
                                onMouseLeave={() => setHoveredMainTaskId(null)}
                                onClick={() => fetchTaskDetail(mainTask.taskId)}
                                >
                                {mainTask.subject}
                                </span>
                                {allSubtasks.length > 0 && (
                                    <span className="text-xs text-gray-400">({allSubtasks.length})</span>
                                )}
                                </div>
                                </td>
                                <td className="px-3 py-2">
                                {(() => {
                                    const { start, end } = getEffectiveDateRange(mainTask, tasks);
                                    if (!start && !end) return <span className="text-sm text-gray-400">-</span>;
                                    return (
                                        <div className="flex items-center gap-1 flex-wrap">
                                        {start && (
                                            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${getStartDateBgColour(start)}`}>
                                            {formatDateUK(start)}
                                            </span>
                                        )}
                                        {start && end && <span className="text-gray-400 text-xs">-</span>}
                                        {end && (
                                            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${getDueDateBgColour(end)}`}>
                                            {formatDateUK(end)}
                                            </span>
                                        )}
                                        </div>
                                    );
                                })()}
                                </td>
                                <td className="px-3 py-2">
                                <span className="text-sm text-gray-600">
                                {project?.name || 'N/A'}
                                </span>
                                </td>
                                <td className="px-3 py-2">
                                {mainTask.assignedToName ? (
                                    <span className="text-sm text-gray-600">
                                    {mainTask.assignedToName}
                                    </span>
                                ) : (
                                    <span className="text-sm text-gray-400">Unassigned</span>
                                )}
                                </td>
                                <td className="px-3 py-2">
                                {mainTask.milestone_name ? (
                                    <span className="text-sm text-gray-600">{mainTask.milestone_name}</span>
                                ) : (
                                    <span className="text-sm text-gray-400">-</span>
                                )}
                                </td>
                                <td className="px-3 py-2">
                                {(() => {
                                    const totalSubtasks = allSubtasks.length;
                                    const completedSubtasks = allSubtasks.filter(t => t.isClosed).length;

                                    // Was keyed off the epic being closed; closure now
                                    // lives on the task itself
                                    if (mainTask.isClosed) {
                                        return <span className="text-xs text-gray-500">Completed</span>;
                                    }
                                    if (totalSubtasks > 0) {
                                        const progress = Math.round((completedSubtasks / totalSubtasks) * 100);
                                        return (
                                            <div className="flex items-center gap-2">
                                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                            <div
                                            className="h-1.5 rounded-full transition-all"
                                            style={{ width: `${progress}%`, background: getProgressGradient('#10b981') }}
                                            />
                                            </div>
                                            <span className="text-xs text-gray-500">
                                            {completedSubtasks}/{totalSubtasks}
                                            </span>
                                            </div>
                                        );
                                    }
                                    return <span className="text-sm text-gray-400">-</span>;
                                })()}
                                </td>
                                </tr>

                                {/* Expanded Subtasks - show all, closed ones have strikethrough, ordered by start date */}
                                {isMainTaskExpanded && (() => {
                                    const sortedSubtasks = [...allSubtasks].sort((a, b) => {
                                        if (!a.start && !b.start) return 0;
                                        if (!a.start) return 1;
                                        if (!b.start) return -1;
                                        return new Date(a.start) - new Date(b.start);
                                    });
                                    return sortedSubtasks.map((subtask, subtaskIdx) => {
                                    const closedStyle = subtask.isClosed ? 'text-gray-400 line-through' : '';
                                    const isLastSubtask = subtaskIdx === sortedSubtasks.length - 1;
                                    const rowBackground = subtask.isMeeting
                                        ? 'linear-gradient(to bottom, #faf6e8 0%, #f5eecc 100%)'
                                        : 'linear-gradient(to bottom, #eff0f3 0%, #f3f4f6 100%)';
                                    const blockers = blockedTasksMap?.get(subtask.taskId?.toString()) || [];
                                    const isBlocked = blockers.length > 0 && !subtask.isClosed;
                                    const hasIssueBlocker = blockers.some(b => b.type === 'issue');
                                    return (
                                    <tr
                                    key={subtask.taskId}
                                    className="hover:bg-gray-200 cursor-pointer"
                                    style={{
                                        background: rowBackground,
                                        ...(isBlocked ? { boxShadow: hasIssueBlocker ? 'inset 3px 0 0 0 #dc2626, inset 5px 0 0 0 #f3f4f6, inset 8px 0 0 0 #dc2626' : 'inset 4px 0 0 0 #dc2626' } : {})
                                    }}
                                    onClick={() => fetchTaskDetail(subtask.taskId)}
                                    >
                                    <td className="px-2 py-1"></td>
                                    <td className="px-3 py-1">
                                    <div className="flex items-center gap-2 pl-8">
                                    <span className={`text-sm ${subtask.isClosed ? 'text-gray-400 line-through' : 'text-gray-600'}`}>{subtask.subject}{subtask.isMeeting && <span className="text-yellow-600 ml-1">(Meeting)</span>}</span>
                                    </div>
                                    </td>
                                    <td className="px-3 py-1">
                                    {subtask.startDate || subtask.dueDate ? (
                                        <div className={`flex items-center gap-1 flex-wrap ${closedStyle}`}>
                                        {subtask.startDate && (
                                            <span className={`inline-flex items-center px-1 py-0.5 text-xs rounded ${subtask.isClosed ? 'bg-gray-100 text-gray-400' : getStartDateBgColour(subtask.startDate)}`}>
                                            {formatDateUK(subtask.startDate)}
                                            </span>
                                        )}
                                        {subtask.startDate && subtask.dueDate && <span className="text-gray-400 text-xs">-</span>}
                                        {subtask.dueDate && (
                                            <span className={`inline-flex items-center px-1 py-0.5 text-xs rounded ${subtask.isClosed ? 'bg-gray-100 text-gray-400' : getDueDateBgColour(subtask.dueDate)}`}>
                                            {formatDateUK(subtask.dueDate)}
                                            </span>
                                        )}
                                        </div>
                                    ) : (
                                        <span className={`text-xs text-gray-400 ${closedStyle}`}>-</span>
                                    )}
                                    </td>
                                    <td className="px-3 py-1">
                                    <span className={`text-xs ${subtask.isClosed ? 'text-gray-400 line-through' : 'text-gray-500'}`}>{project?.name || 'N/A'}</span>
                                    </td>
                                    <td className="px-3 py-1">
                                    {subtask.assignedToName ? (
                                        <span className={`text-xs ${subtask.isClosed ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
                                        {subtask.assignedToName}
                                        </span>
                                    ) : (
                                        <span className={`text-xs text-gray-400 ${closedStyle}`}>Unassigned</span>
                                    )}
                                    </td>
                                    <td className="px-3 py-1">
                                    {isBlocked ? (() => {
                                        const issueBlocker = blockers.find(b => b.type === 'issue');
                                        return (
                                        <span
                                            className="text-xs font-medium text-red-600"
                                            onMouseEnter={(e) => handleBlockedMouseEnter(e, blockers)}
                                            onMouseLeave={handleBlockedMouseLeave}
                                        >
                                            Blocked{issueBlocker && ` (${issueBlocker.id})`}
                                        </span>
                                        );
                                    })() : (() => {
                                        const statusName = subtask.statusName || 'New';
                                        const progress = getSubtaskProgress(statusName, subtask.isClosed);
                                        return (
                                            <span
                                            className={`text-xs font-medium ${subtask.isClosed ? 'line-through' : ''}`}
                                            style={{ color: progress.colour }}
                                            >
                                            {statusName}
                                            </span>
                                        );
                                    })()}
                                    </td>
                                    <td className="px-3 py-1">
                                    {subtask.isClosed ? (
                                        <span className="text-xs text-gray-500">Completed</span>
                                    ) : (() => {
                                        const statusName = subtask.statusName || 'New';
                                        const progress = getSubtaskProgress(statusName, subtask.isClosed);
                                        return (
                                            <div className="flex items-center gap-3">
                                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                            <div
                                            className="h-1.5 rounded-full"
                                            style={{
                                                width: `${progress.percent}%`,
                                                background: getProgressGradient(progress.colour),
                                                animation: progress.needsInfo ? 'needsInfoPulse 2s ease-in-out infinite' : 'none'
                                            }}
                                            />
                                            </div>
                                            <span className="text-xs text-gray-600 min-w-[3rem] text-right">
                                            {progress.percent}%
                                            </span>
                                            </div>
                                        );
                                    })()}
                                    </td>
                                    </tr>
                                    );
                                });
                                })()}
                                </React.Fragment>
                            );
                        })}

                        {/* No tasks message */}
                        {isExpanded && statusTasks.length === 0 && (
                            <tr className="bg-gray-50">
                            <td className="px-2 py-2"></td>
                            <td colSpan="6" className="px-3 py-2 text-sm text-gray-500 italic">
                            No tasks in this status
                            </td>
                            </tr>
                        )}
                        </React.Fragment>
                    );
                })}

                {/* Epic Grouping - for tasks assigned to Epics */}
                {epics.filter(epic => {
                    const tasksWithThisEpic = (tasks || []).filter(t => !t.mainTaskId && t.epics && t.epics.some(e => e.id === epic.epicId));
                    return tasksWithThisEpic.some(t => tasks.some(st => st.mainTaskId === t.taskId));
                }).map((epic) => {
                    // Unfiltered set - drives the completed-group marker
                    const allEpicTasks = (tasks || [])
                        .filter(t => !t.mainTaskId && t.epics && t.epics.some(e => e.id === epic.epicId))
                        .filter(mainTask => tasks.some(t => t.mainTaskId === mainTask.taskId));
                    const groupComplete = allEpicTasks.length > 0 && allEpicTasks.every(t => t.isClosed === true);

                    // Visible set - rows, counts and progress follow the toggle.
                    // The .filter() above stays unfiltered so the header survives.
                    const epicTasks = hideCompletedEpics
                        ? allEpicTasks.filter(t => t.isClosed !== true)
                        : allEpicTasks;
                    const isExpanded = collapsedColumns.has(`epic-${epic.epicId}`);

                    // Calculate epic progress from all subtasks
                    const allEpicSubtasks = epicTasks.flatMap(t => tasks.filter(st => st.mainTaskId === t.taskId));
                    const epicProgress = allEpicSubtasks.length > 0
                        ? Math.round((allEpicSubtasks.filter(st => st.isClosed).length / allEpicSubtasks.length) * 100)
                        : 0;

                    return (
                        <React.Fragment key={`epic-${epic.epicId}`}>
                        {/* Epic Row - same styling as Status rows */}
                        <tr
                            className="hover:bg-purple-100 font-medium"
                            style={{
                                background: 'linear-gradient(to bottom, #faf7ff 0%, #f5f5f6 100%)',
                                boxShadow: 'inset 0 2px 0 0 #e9d5ff, inset 0 -1px 0 0 #e9d5ff'
                            }}
                        >
                            <td className="px-2 py-3">
                            <button
                            onClick={() => {
                                const newCollapsed = new Set(collapsedColumns);
                                if (isExpanded) {
                                    newCollapsed.delete(`epic-${epic.epicId}`);
                                } else {
                                    newCollapsed.add(`epic-${epic.epicId}`);
                                }
                                setCollapsedColumns(newCollapsed);
                            }}
                            className="text-gray-600 hover:text-gray-900"
                            >
                                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                            </button>
                            </td>
                            <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                            {groupComplete && <FaCheck className="text-green-600 flex-shrink-0" size={12} />}
                            <span className={`font-medium ${groupComplete ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{epic.subject}</span>
                            <span className="text-xs text-gray-500">({epicTasks.length})</span>
                            </div>
                            </td>
                            <td className="px-3 py-3">
                            <span className="text-sm text-gray-400">-</span>
                            </td>
                            <td className="px-3 py-3">
                            <span className="text-sm text-gray-400">-</span>
                            </td>
                            <td className="px-3 py-3">
                            <span className="text-sm text-gray-400">-</span>
                            </td>
                            <td className="px-3 py-3">
                            <span className="text-sm text-gray-400">-</span>
                            </td>
                            <td className="px-3 py-3">
                            <div className="flex items-center gap-3">
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div
                                    className="h-2 rounded-full transition-all"
                                    style={{ width: `${epicProgress}%`, background: getProgressGradient('#7c6b9e') }}
                                />
                                </div>
                                <span className="text-xs text-gray-600 min-w-[3rem] text-right">{epicProgress}%</span>
                            </div>
                            </td>
                        </tr>

                        {/* Expanded Tasks within Epic */}
                        {isExpanded && [...epicTasks].sort((a, b) => {
                            const swimlaneDiff = (a.swimlaneOrder || 0) - (b.swimlaneOrder || 0);
                            if (swimlaneDiff !== 0) return swimlaneDiff;
                            return (a.order || 0) - (b.order || 0);
                        }).map((mainTask) => {
                            const allSubtasks = tasks.filter(t => t.mainTaskId === mainTask.taskId);
                            const isMainTaskExpanded = collapsedColumns.has(`epic-maintask-${mainTask.taskId}`);
                            const isTeamTask = mainTask.swimlaneId === teamSwimlaneId || (mainTask.swimlaneId === null && teamSwimlaneId === project?.default_swimlane);

                            return (
                                <React.Fragment key={`epic-maintask-${mainTask.taskId}`}>
                                <tr
                                    className={`hover:bg-gray-100 ${hoveredMainTaskId === mainTask.taskId ? 'bg-yellow-50 shadow-[inset_4px_0_0_0_#facc15]' : ''}`}
                                    style={hoveredMainTaskId !== mainTask.taskId ? {
                                        background: isTeamTask ? 'linear-gradient(to bottom, #eff6ff 0%, #f3f4f6 100%)' : 'linear-gradient(to bottom, #f3fef6 0%, #f5f5f6 100%)',
                                        boxShadow: isTeamTask
                                            ? 'inset 3px 0 0 0 #3b82f6, inset 0 2px 0 0 #93c5fd'
                                            : 'inset 0 2px 0 0 #86efac'
                                    } : {}}
                                >
                                <td className="px-2 py-2">
                                {allSubtasks.length > 0 && (
                                    <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const newCollapsed = new Set(collapsedColumns);
                                        if (isMainTaskExpanded) {
                                            newCollapsed.delete(`epic-maintask-${mainTask.taskId}`);
                                        } else {
                                            newCollapsed.add(`epic-maintask-${mainTask.taskId}`);
                                        }
                                        setCollapsedColumns(newCollapsed);
                                    }}
                                    className="text-gray-600 hover:text-gray-900 ml-2"
                                    >
                                    {isMainTaskExpanded ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}
                                    </button>
                                )}
                                </td>
                                <td className="px-3 py-2">
                                <div className="flex items-center gap-2 pl-4">
                                {mainTask.isClosed && <FaCheck className="text-green-600 flex-shrink-0" size={11} />}
                                <span
                                className={`text-sm hover:text-blue-600 cursor-pointer ${mainTask.isClosed ? 'text-gray-400 line-through' : 'text-gray-700'}`}
                                onMouseEnter={() => setHoveredMainTaskId(mainTask.taskId)}
                                onMouseLeave={() => setHoveredMainTaskId(null)}
                                onClick={() => fetchTaskDetail(mainTask.taskId)}
                                >
                                {mainTask.subject}
                                </span>
                                {allSubtasks.length > 0 && (
                                    <span className="text-xs text-gray-400">({allSubtasks.length})</span>
                                )}
                                </div>
                                </td>
                                <td className="px-3 py-2">
                                {(() => {
                                    const { start, end } = getEffectiveDateRange(mainTask, tasks);
                                    if (!start && !end) return <span className="text-sm text-gray-400">-</span>;
                                    return (
                                        <div className="flex items-center gap-1 flex-wrap">
                                        {start && (
                                            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${getStartDateBgColour(start)}`}>
                                            {formatDateUK(start)}
                                            </span>
                                        )}
                                        {start && end && <span className="text-gray-400 text-xs">-</span>}
                                        {end && (
                                            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${getDueDateBgColour(end)}`}>
                                            {formatDateUK(end)}
                                            </span>
                                        )}
                                        </div>
                                    );
                                })()}
                                </td>
                                <td className="px-3 py-2">
                                <span className="text-sm text-gray-600">{project?.name || 'N/A'}</span>
                                </td>
                                <td className="px-3 py-2">
                                {mainTask.assignedToName ? (
                                    <span className="text-sm text-gray-600">
                                    {mainTask.assignedToName}
                                    </span>
                                ) : (
                                    <span className="text-sm text-gray-400">Unassigned</span>
                                )}
                                </td>
                                <td className="px-3 py-2">
                                {mainTask.milestone_name ? (
                                    <span className="text-sm text-gray-600">{mainTask.milestone_name}</span>
                                ) : (
                                    <span className="text-sm text-gray-400">-</span>
                                )}
                                </td>
                                <td className="px-3 py-2">
                                {(() => {
                                    const totalSubtasks = allSubtasks.length;
                                    const completedSubtasks = allSubtasks.filter(t => t.isClosed).length;
                                    if (totalSubtasks > 0) {
                                        const progress = Math.round((completedSubtasks / totalSubtasks) * 100);
                                        return (
                                            <div className="flex items-center gap-2">
                                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                            <div
                                            className="h-1.5 rounded-full transition-all"
                                            style={{ width: `${progress}%`, background: getProgressGradient('#10b981') }}
                                            />
                                            </div>
                                            <span className="text-xs text-gray-500">{completedSubtasks}/{totalSubtasks}</span>
                                            </div>
                                        );
                                    }
                                    return <span className="text-sm text-gray-400">-</span>;
                                })()}
                                </td>
                                </tr>

                                {/* Subtasks within Epic task */}
                                {isMainTaskExpanded && [...allSubtasks].sort((a, b) => {
                                    if (!a.start && !b.start) return 0;
                                    if (!a.start) return 1;
                                    if (!b.start) return -1;
                                    return new Date(a.start) - new Date(b.start);
                                }).map((subtask) => {
                                    const closedStyle = subtask.isClosed ? 'text-gray-400 line-through' : '';
                                    const rowBackground = subtask.isMeeting
                                        ? 'linear-gradient(to bottom, #faf6e8 0%, #f5eecc 100%)'
                                        : 'linear-gradient(to bottom, #eff0f3 0%, #f3f4f6 100%)';
                                    const blockers = blockedTasksMap?.get(subtask.taskId?.toString()) || [];
                                    const isBlocked = blockers.length > 0 && !subtask.isClosed;
                                    const hasIssueBlocker = blockers.some(b => b.type === 'issue');
                                    return (
                                    <tr
                                    key={`epic-subtask-${subtask.taskId}`}
                                    className="hover:bg-gray-200 cursor-pointer"
                                    style={{
                                        background: rowBackground,
                                        ...(isBlocked ? { boxShadow: hasIssueBlocker ? 'inset 3px 0 0 0 #dc2626, inset 5px 0 0 0 #f3f4f6, inset 8px 0 0 0 #dc2626' : 'inset 4px 0 0 0 #dc2626' } : {})
                                    }}
                                    onClick={() => fetchTaskDetail(subtask.taskId)}
                                    >
                                    <td className="px-2 py-1"></td>
                                    <td className="px-3 py-1">
                                    <div className="flex items-center gap-2 pl-8">
                                    <span className={`text-sm ${subtask.isClosed ? 'text-gray-400 line-through' : 'text-gray-600'}`}>{subtask.subject}{subtask.isMeeting && <span className="text-yellow-600 ml-1">(Meeting)</span>}</span>
                                    </div>
                                    </td>
                                    <td className="px-3 py-1">
                                    {subtask.startDate || subtask.dueDate ? (
                                        <div className={`flex items-center gap-1 flex-wrap ${closedStyle}`}>
                                        {subtask.startDate && (
                                            <span className={`inline-flex items-center px-1 py-0.5 text-xs rounded ${subtask.isClosed ? 'bg-gray-100 text-gray-400' : getStartDateBgColour(subtask.startDate)}`}>
                                            {formatDateUK(subtask.startDate)}
                                            </span>
                                        )}
                                        {subtask.startDate && subtask.dueDate && <span className="text-gray-400 text-xs">-</span>}
                                        {subtask.dueDate && (
                                            <span className={`inline-flex items-center px-1 py-0.5 text-xs rounded ${subtask.isClosed ? 'bg-gray-100 text-gray-400' : getDueDateBgColour(subtask.dueDate)}`}>
                                            {formatDateUK(subtask.dueDate)}
                                            </span>
                                        )}
                                        </div>
                                    ) : (
                                        <span className={`text-xs text-gray-400 ${closedStyle}`}>-</span>
                                    )}
                                    </td>
                                    <td className="px-3 py-1">
                                    <span className={`text-xs ${subtask.isClosed ? 'text-gray-400 line-through' : 'text-gray-500'}`}>{project?.name || 'N/A'}</span>
                                    </td>
                                    <td className="px-3 py-1">
                                    {subtask.assignedToName ? (
                                        <span className={`text-xs ${subtask.isClosed ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
                                        {subtask.assignedToName}
                                        </span>
                                    ) : (
                                        <span className={`text-xs text-gray-400 ${closedStyle}`}>Unassigned</span>
                                    )}
                                    </td>
                                    <td className="px-3 py-1">
                                    {isBlocked ? (() => {
                                        const issueBlocker = blockers.find(b => b.type === 'issue');
                                        return (
                                        <span
                                            className="text-xs font-medium text-red-600"
                                            onMouseEnter={(e) => handleBlockedMouseEnter(e, blockers)}
                                            onMouseLeave={handleBlockedMouseLeave}
                                        >
                                            Blocked{issueBlocker && ` (${issueBlocker.id})`}
                                        </span>
                                        );
                                    })() : (() => {
                                        const statusName = subtask.statusName || 'New';
                                        const progress = getSubtaskProgress(statusName, subtask.isClosed);
                                        return (
                                            <span className={`text-xs ${subtask.isClosed ? 'text-gray-400 line-through' : ''}`} style={{ color: subtask.isClosed ? undefined : progress.colour }}>
                                            {statusName}
                                            </span>
                                        );
                                    })()}
                                    </td>
                                    <td className="px-3 py-1">
                                    {(() => {
                                        const statusName = subtask.statusName || 'New';
                                        const progress = getSubtaskProgress(statusName, subtask.isClosed);
                                        return (
                                            <div className="flex items-center gap-2">
                                            <div className={`flex-1 bg-gray-200 rounded-full h-1.5 ${progress.needsInfo && !subtask.isClosed ? 'animate-pulse' : ''}`}>
                                            <div
                                            className="h-1.5 rounded-full transition-all"
                                            style={{ width: `${progress.percent}%`, background: getProgressGradient(progress.colour) }}
                                            />
                                            </div>
                                            <span className="text-xs text-gray-500">{progress.percent}%</span>
                                            </div>
                                        );
                                    })()}
                                    </td>
                                    </tr>
                                    );
                                })}
                                </React.Fragment>
                            );
                        })}
                        </React.Fragment>
                    );
                })}
                </tbody>
                </table>
                </div>
            )}
            </div>
            </div>
        );
        })()}

        {/* Kanban Board */}
        <div className="flex-1 overflow-hidden min-h-0">
        <div className="w-full h-full mx-auto p-2">
        <KanbanBoard
        project={project}
        tasks={tasks}
        columns={columns}
        members={members}
        collapsedColumns={collapsedColumns}
        setCollapsedColumns={setCollapsedColumns}
        collapsedSwimlanes={collapsedSwimlanes}
        setCollapsedSwimlanes={setCollapsedSwimlanes}
        hoveredMainTaskId={hoveredMainTaskId}
        setHoveredMainTaskId={setHoveredMainTaskId}
        teamSwimlaneId={teamSwimlaneId}
        draggedTask={draggedTask}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={(statusId) => handleDrop(draggedTaskRef.current, statusId, columns, updateInProgressRef)}
        onTaskClick={fetchTaskDetail}
        onCreateTaskClick={handleOpenCreateModal}
        isAdmin={isAdmin}
        lead={lead}
        edgeColumnsInitialised={edgeColumnsInitialisedRef.current}
        blockedTasksMap={blockedTasksMap}
        />

        {/* Empty State */}
        {columns.length === 0 && (
            <div className="bg-white rounded-lg shadow p-12 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Your project is being set up
            </h3>
            <p className="text-gray-600">
            Tasks will appear here once your migration project begins
            </p>
            </div>
        )}
        </div>
        </div>

        {/* Footer Info */}
        {/* Task Detail Modal */}
        <TaskDetailsModal
        task={selectedTaskDetail}
        isOpen={!!selectedTaskDetail}
        onClose={() => {
            preventRefreshRef.current = true;
            setSelectedTaskDetail(null);
            selectedTaskRef.current = null;
            setTimeout(() => {
                preventRefreshRef.current = false;
            }, 2000);
        }}
        onUpdate={(taskId, updates) => {
            const { updates: clamped, adjustments, impossible, endBeforeStart } =
                clampTaskDateUpdates(tasks, links, taskId, updates);
            if (endBeforeStart) { alertEndBeforeStart(endBeforeStart); return; }
            if (impossible) { alertImpossibleRange(impossible); return; }
            if (!confirmClampAdjustments(adjustments)) return;
            const { proceed, override } = confirmProjectWindow({
                start: clamped.start,
                dueDate: clamped.due_date || clamped.dueDate,
                kickoffDate: project?.kickoffDate,
                label: 'This task'
            });
            if (!proceed) return;
            return handleSaveChanges(taskId, override ? { ...clamped, allowBeforeKickoff: true } : clamped);
        }}
        onDelete={handleDeleteTask}
        onCommentAdd={handleAddComment}
        onCommentEdit={handleEditComment}
        onCommentDelete={handleDeleteComment}
        onSubtaskCreate={(parentTaskId, subtaskData) => {
            const { proceed, override } = confirmProjectWindow({
                start: subtaskData.start,
                dueDate: subtaskData.dueDate,
                kickoffDate: project?.kickoffDate,
                label: subtaskData.subject || 'This subtask'
            });
            if (!proceed) return;
            return handleCreateSubtask(parentTaskId, override ? { ...subtaskData, allowBeforeKickoff: true } : subtaskData);
        }}
        onSubtaskClick={fetchTaskDetail}
        isLoading={loadingTaskDetail}
        projectName={project?.name}
        columns={columns}
        taskStatuses={taskStatuses}  // ADD THIS LINE
        teamSwimlaneId={teamSwimlaneId}
        members={members}
        tasks={tasks}
        lead={lead}
        savingChanges={savingChanges}
        savingField={savingField}
        addingComment={addingComment}
        editingComment={editingComment}
        deletingComment={deletingComment}
        creatingSubtask={creatingSubtask}
        projectMembers={projectMembers}
        onOpenAssignmentEditor={handleOpenAssignmentEditor}
        blockedTasksMap={blockedTasksMap}
        />

        {/* Loading Modal - only show when no task modal is already open */}
        {loadingTaskDetail && !selectedTaskDetail && (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8">
            <div className="text-center">
            <div className="text-xl text-gray-600">Loading task details...</div>
            </div>
            </div>
            </div>
        )}

        <CreateTaskModal
        isOpen={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        onCreate={handleCreateTask}
        projectName={project?.name}
        isCreating={creatingTask}
        members={clientUsers}  // Change this
        lead={lead}
        />

        {/* Blocked tooltip */}
        {blockedTooltip && (
            <div
                className="fixed z-50 px-3 py-2 text-sm bg-gray-800 text-white rounded-lg max-w-xs"
                style={{
                    left: blockedTooltip.x,
                    top: blockedTooltip.y,
                    transform: 'translate(-50%, -100%)',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)'
                }}
                onMouseEnter={() => {
                    if (blockedTooltipTimeoutRef.current) clearTimeout(blockedTooltipTimeoutRef.current);
                }}
                onMouseLeave={() => setBlockedTooltip(null)}
            >
                <div className="font-bold text-red-400">Blocked by:</div>
                {blockedTooltip.content.map((b, i) => (
                    <div key={i} className="pl-2 text-red-300">
                        {b.type === 'issue'
                            ? `Issue ${b.id}: ${b.title}`
                            : b.parentTitle
                                ? `${b.parentTitle} / ${b.title}`
                                : b.title
                        }
                    </div>
                ))}
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800" />
            </div>
        )}

        </div>
    );
};

export default ClientDashboard;
